import React, { useEffect, useMemo, useState } from "react";
import { listarGastosMes, crearGastoMes, pagosComparativa } from "../api";
import BalanceComparativa from "./BalanceComparativa";
import "./BalanceTab.css";

export default function BalanceTab({
  mes,
  setMes,
  ingresos,
  pagosMes = [],
  sesionesPendientes = [],
}) {
  const [gastos, setGastos] = useState([]);
  const [totalGastos, setTotalGastos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [nuevoConcepto, setNuevoConcepto] = useState("");
  const [nuevoImporte, setNuevoImporte] = useState("");

  const [subTab, setSubTab] = useState("resumen");

  const [comparativaData, setComparativaData] = useState([]);
  const [comparativaLoading, setComparativaLoading] = useState(false);
  const [comparativaError, setComparativaError] = useState("");

  const mesTexto = useMemo(() => {
    if (!mes) return "";
    const d = new Date(mes + "-01");
    if (Number.isNaN(d.getTime())) return mes;
    return d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
  }, [mes]);

  const selectedYear = useMemo(() => {
    const y = Number(String(mes || "").slice(0, 4));
    return Number.isFinite(y) && y > 2000 ? y : new Date().getFullYear();
  }, [mes]);

  const normCode = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[_\s]+/g, "-");

  const getSpecialtyCode = (item) =>
    item?.specialty_code ||
    item?.specialty ||
    item?.specialty_name ||
    item?.especialidad ||
    "";

  const getFactorCentro = (codeRaw) => {
    const code = normCode(codeRaw);

    if (code === "logopeda" || code === "nutricion") return 0.3;
    if (code === "psicologia-g" || code === "psicologia-m") return 0.4;
    if (code === "psicologia") return 1;

    return 1;
  };

  const ingresosRealCentro = useMemo(() => {
    if (!Array.isArray(pagosMes) || pagosMes.length === 0) {
      return Number(ingresos || 0);
    }

    return pagosMes.reduce((acc, p) => {
      const amt = Number(p.amount) || 0;
      const factor = getFactorCentro(getSpecialtyCode(p));
      return acc + amt * factor;
    }, 0);
  }, [pagosMes, ingresos]);

  const ingresosNum = useMemo(
    () => Number(ingresosRealCentro || 0),
    [ingresosRealCentro]
  );

  const balance = useMemo(
    () => ingresosNum - Number(totalGastos || 0),
    [ingresosNum, totalGastos]
  );

  const cargarGastos = async (month) => {
    if (!month) return;

    try {
      setLoading(true);
      setError("");
      const data = await listarGastosMes(month);
      setGastos(data.items || []);
      setTotalGastos(data.total || 0);
    } catch (e) {
      console.error(e);
      setError(e.message || "Error al cargar gastos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mes) cargarGastos(mes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mes]);

  const handleAddGasto = async () => {
    const imp = parseFloat(String(nuevoImporte).replace(",", "."));

    if (!nuevoConcepto.trim()) {
      alert("Introduce un concepto de gasto (ej. Alquiler, Luz...)");
      return;
    }

    if (Number.isNaN(imp) || imp <= 0) {
      alert("Introduce un importe válido mayor que 0");
      return;
    }

    try {
      await crearGastoMes({
        mes,
        concepto: nuevoConcepto.trim(),
        importe: imp,
      });

      setNuevoConcepto("");
      setNuevoImporte("");
      await cargarGastos(mes);
    } catch (e) {
      alert(e.message || "Error al guardar gasto");
    }
  };

  const PRECIOS = useMemo(
    () => ({
      consulta_inicial: 0,
      reeducacion_psicopedagogica_individual: 45,
      estimulacion: 45,
      psicologia_infantojuvenil_individual: 50,
      intervencion_psicoterapia_mes: 200,
      intervencion_estimulacion_mes: 180,
      intervencion_logopedia_mes: 140,
      intervencion_deglucion_mes: 120,
      terapia_familiar_individual: 60,
      psicologia_perinatal_individual: 50,
      asesoramiento_crianza_individual: 45,
      sesion_logopedia_individual: 35,
      clase_yoga: 15,
      atencion_temprana_individual: 45,
      entrevista_inicial: 30,
      evaluacion_psicologica: 350,
      evaluacion_logopedia: 150,
      deglucion: 25,
      intervencion_reeducacion_NEAE_mes: 180,
      intervencion_logopedia_NEAE_mes: 140,

      psicoterapia: 50,
      logopedia: 35,
      reeducacion_psicopedagogica: 45,
    }),
    []
  );

  const normalizePrecio = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

  const toNum = (v) => {
    const n = Number(String(v ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const getPrecioSesion = (s) => {
    if (!s) return 0;

    const direct = s?.precio ?? s?.importe ?? s?.amount ?? s?.tarifa;
    const n = toNum(direct);
    if (n !== null && n > 0) return n;

    const key = normalizePrecio(s?.service_code || s?.servicio);

    if (key && Object.prototype.hasOwnProperty.call(PRECIOS, key)) {
      return Number(PRECIOS[key]) || 0;
    }

    if (key.includes("psicoter")) return Number(PRECIOS.psicoterapia || 0);
    if (key.includes("logoped")) return Number(PRECIOS.logopedia || 0);
    if (key.includes("reeduc")) {
      return Number(PRECIOS.reeducacion_psicopedagogica || 0);
    }
    if (key.includes("evaluacion_psicologica")) {
      return Number(PRECIOS.evaluacion_psicologica || 0);
    }
    if (key.includes("evaluacion_logopedia")) {
      return Number(PRECIOS.evaluacion_logopedia || 0);
    }

    return 0;
  };

  const ingresosPendientesCalculados = useMemo(() => {
    if (!Array.isArray(sesionesPendientes) || sesionesPendientes.length === 0) {
      return 0;
    }

    return sesionesPendientes.reduce((acc, s) => {
      const precio = getPrecioSesion(s);
      const factor = getFactorCentro(getSpecialtyCode(s));
      return acc + precio * factor;
    }, 0);
  }, [sesionesPendientes, PRECIOS]);

  const prevision = useMemo(() => {
    const gastosNum = Number(totalGastos || 0);
    const ingresosCobrados = ingresosNum;
    const ingresosPend = Number(ingresosPendientesCalculados || 0);

    const ingresosProyectados = ingresosCobrados + ingresosPend;
    const balanceProyectado = ingresosProyectados - gastosNum;
    const faltanteParaEquilibrio = Math.max(0, gastosNum - ingresosProyectados);

    return {
      ingresosCobrados,
      ingresosPendientes: ingresosPend,
      ingresosProyectados,
      gastosProyectados: gastosNum,
      balanceProyectado,
      faltanteParaEquilibrio,
      sesionesPendientesCount: Array.isArray(sesionesPendientes)
        ? sesionesPendientes.length
        : 0,
    };
  }, [
    ingresosNum,
    ingresosPendientesCalculados,
    totalGastos,
    sesionesPendientes,
  ]);

  useEffect(() => {
    if (subTab !== "comparativa") return;

    let alive = true;

    (async () => {
      try {
        setComparativaLoading(true);
        setComparativaError("");

        const items = await pagosComparativa(selectedYear);

        if (!alive) return;
        setComparativaData(items || []);
      } catch (e) {
        if (!alive) return;
        setComparativaError(e?.message || "Error al cargar comparativa");
        setComparativaData([]);
      } finally {
        if (alive) setComparativaLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [subTab, selectedYear]);

  return (
    <div className="balance-tab">
      <div className="balance-header">
        <h3>📊 Balance del mes {mesTexto || mes}</h3>

        <div className="balance-mes-filter">
          <label>
            Mes:
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="balance-subtabs">
        <button
          type="button"
          className={subTab === "resumen" ? "active" : ""}
          onClick={() => setSubTab("resumen")}
        >
          Resumen
        </button>

        <button
          type="button"
          className={subTab === "prevision" ? "active" : ""}
          onClick={() => setSubTab("prevision")}
        >
          Previsión
        </button>

        <button
          type="button"
          className={subTab === "comparativa" ? "active" : ""}
          onClick={() => setSubTab("comparativa")}
        >
          Comparativa
        </button>
      </div>

      {subTab === "resumen" && (
        <>
          <div className="balance-resumen">
            <div className="balance-card ingresos">
              <span>Ingresos reales del centro</span>
              <strong>{ingresosNum.toFixed(2)} €</strong>
            </div>

            <div className="balance-card gastos">
              <span>Gastos del mes</span>
              <strong>{Number(totalGastos || 0).toFixed(2)} €</strong>
            </div>

            <div
              className={`balance-card balance-neto ${
                balance >= 0 ? "positivo" : "negativo"
              }`}
            >
              <span>Balance</span>
              <strong>{balance.toFixed(2)} €</strong>
            </div>
          </div>

          <div className="balance-add">
            <h4>Gastos del mes</h4>

            <div className="balance-add-row">
              <input
                type="text"
                placeholder="Concepto (ej. Alquiler, Luz...)"
                value={nuevoConcepto}
                onChange={(e) => setNuevoConcepto(e.target.value)}
              />

              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Importe €"
                value={nuevoImporte}
                onChange={(e) => setNuevoImporte(e.target.value)}
              />

              <button
                type="button"
                className="btn-add"
                onClick={handleAddGasto}
                title="Añadir gasto"
              >
                ➕
              </button>
            </div>
          </div>

          {loading ? (
            <p>Cargando gastos...</p>
          ) : error ? (
            <p className="balance-error">{error}</p>
          ) : gastos.length === 0 ? (
            <p>No hay gastos registrados para este mes.</p>
          ) : (
            <div className="pv-table-wrapper" style={{ marginTop: "0.75rem" }}>
              <table className="pv-table">
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th>Importe (€)</th>
                    <th>Fecha registro</th>
                  </tr>
                </thead>

                <tbody>
                  {gastos.map((g) => (
                    <tr key={g.id}>
                      <td>{g.concepto}</td>
                      <td>{Number(g.importe).toFixed(2)} €</td>
                      <td>
                        {g.created_at
                          ? new Date(g.created_at).toLocaleDateString("es-ES")
                          : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {subTab === "prevision" && (
        <div className="balance-prevision">
          <h4>Previsión real del mes</h4>

          <div className="balance-resumen">
            <div className="balance-card ingresos">
              <span>Ingresos reales previstos</span>
              <strong>{prevision.ingresosProyectados.toFixed(2)} €</strong>
            </div>

            <div className="balance-card gastos">
              <span>Gastos previstos</span>
              <strong>{prevision.gastosProyectados.toFixed(2)} €</strong>
            </div>

            <div
              className={`balance-card balance-neto ${
                prevision.balanceProyectado >= 0 ? "positivo" : "negativo"
              }`}
            >
              <span>Balance previsto</span>
              <strong>{prevision.balanceProyectado.toFixed(2)} €</strong>
            </div>
          </div>

          <p className="balance-prevision-text">
            De los ingresos reales previstos para este mes (
            <strong>{prevision.ingresosProyectados.toFixed(2)} €</strong>), ya
            tienes cobrados{" "}
            <strong>{prevision.ingresosCobrados.toFixed(2)} €</strong> y quedan
            pendientes de cobro aproximadamente{" "}
            <strong>{prevision.ingresosPendientes.toFixed(2)} €</strong>.
          </p>

          <p className="balance-prevision-text">
            Sesiones pendientes contadas:{" "}
            <strong>{prevision.sesionesPendientesCount}</strong>
          </p>

          {prevision.faltanteParaEquilibrio > 0 ? (
            <p className="balance-prevision-text">
              Para cubrir todos los gastos previstos te faltarían unos{" "}
              <strong>{prevision.faltanteParaEquilibrio.toFixed(2)} €</strong>.
            </p>
          ) : (
            <p className="balance-prevision-text">
              Con los ingresos reales previstos, cubrirías todos los gastos del
              mes y cerrarías con balance positivo.
            </p>
          )}
        </div>
      )}

      {subTab === "comparativa" && (
        <div style={{ marginTop: 12 }}>
          {comparativaLoading ? <p>Cargando comparativa…</p> : null}

          {comparativaError ? (
            <p className="balance-error">{comparativaError}</p>
          ) : null}

          {!comparativaLoading && !comparativaError ? (
            <BalanceComparativa
              data={comparativaData}
              principalYear={selectedYear}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}